import { ROLE_LABEL } from "./roleOptions";
import { formatISTDate } from "./datetime";

/**
 * A26/A28: master writes (doctors, distributors, products, territories,
 * team/users) FILE a master change instead of applying. The backend decides —
 * the response carries status "Submitted" and the approving role it derived
 * from the chain — and every master page branches its success toast on this
 * (the TeamChangeDialogs pattern). One helper so the wording and the branch
 * cannot drift page by page.
 */
export function isPendingMasterChange(data) {
  return Boolean(data && data.master && data.status === "Submitted");
}

/**
 * WHO HOLDS IT, AS A PERSON. Never a role literal.
 *
 * `waiting_on` is stamped by the backend from `team_scope.waiting_on`, which
 * confirms every candidate with `can_approve_submission` — the same rule that
 * governs the Approve button. So the name shown here and the permission that
 * decides the request are one answer, and a hierarchy change moves both at once.
 * A literal "Director" in this file would be wrong for the reciprocal branch
 * TODAY (a change the Director files goes to the Admin Manager) and wrong for
 * everybody the day the deep-hierarchy work lands.
 *
 * An EMPTY approvers list is a real answer — "nobody can act on this" — and is
 * said out loud rather than rendered as a blank, the reading `waiting_on` asks
 * every caller to take.
 */
export function approverPhrase(waitingOn) {
  const role = waitingOn?.approver_role;
  const roleLabel = ROLE_LABEL[role] || role;
  const names = (waitingOn?.approvers || []).map((a) => a?.name).filter(Boolean);
  if (names.length) {
    const joined =
      names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
    return roleLabel ? `${joined} (${roleLabel})` : joined;
  }
  if (roleLabel) return `nobody — there is no active ${roleLabel} to approve it`;
  return "nobody — no approver could be resolved";
}

/**
 * THE DESTINATION, NAMED BEFORE THE ACT.
 *
 * The Archive dialog described an end state in the present tense — "their
 * sign-in is revoked" — while the endpoint files a request for a second
 * signature. All eleven duplicate presses of 24 August happened because of that
 * sentence. Any control that FILES rather than DOES says this first, with the
 * approver resolved from the chain through `/master-changes/approver`.
 */
export function willBeSentForApprovalMessage(waitingOn) {
  return `This will be sent to ${approverPhrase(waitingOn)} for approval.`;
}

/** THE DESTINATION, NAMED AFTER THE ACT — the filing receipt's toast. */
export function sentForApprovalMessage(data) {
  // The stamped `waiting_on` is the person; `approver_role` is the older,
  // role-only hint the backend still stamps at filing. Prefer the person, fall
  // back to the role rather than to a constant.
  if (data?.waiting_on) return `Filed — waiting on ${approverPhrase(data.waiting_on)}`;
  const role = ROLE_LABEL[data?.approver_role] || data?.approver_role || "an approver";
  return `Filed — waiting on the ${role}`;
}

/**
 * WHAT A FILED MASTER CHANGE LOOKS LIKE ON THE PAGE IT WAS FILED FROM.
 *
 * The toast above says it once and then it is gone. A master page that shows
 * nothing afterwards is indistinguishable from one whose write was lost — which
 * is exactly how `POST /api/users/{id}/archive` was reported: 200, the row still
 * in the Team list, no visible trace, three times over. The write was there the
 * whole time, in `master_changes`; nothing on the page said so.
 *
 * `pendingMasterLabel` is the standing line. Keyed by action so a new one has
 * to be named rather than silently rendering "Change pending approval". It
 * names the HOLDER and the AGE, because "pending approval — Director" told the
 * filer neither whether anyone could act nor whether anyone had.
 */
const MASTER_ACTION_LABEL = {
  archive: "Archive",
  restore: "Restore",
  update: "Edit",
};

export function pendingMasterLabel(change) {
  const action = MASTER_ACTION_LABEL[change?.action] || "Change";
  const since = change?.created_at ? ` since ${formatISTDate(change.created_at)}` : "";
  if (change?.waiting_on) {
    return `${action} pending with ${approverPhrase(change.waiting_on)}${since}`;
  }
  const role = ROLE_LABEL[change?.approver_role] || change?.approver_role;
  return role
    ? `${action} pending approval — ${role}${since}`
    : `${action} pending approval${since}`;
}
